// Planning (Phase 5, per Adam's framing): the global annual strategy at
// the top, then the quarter beneath it — theme + KPI targets from
// content_quarters (edited on the Content Plan tab) and the projects due
// that quarter. Every 90 days the ritual is: review strategy, set the
// quarter's theme + targets, line up its projects and content.
//
// Strategies and avatars are DB-backed and editable (arrays edit as one
// item per line). The old read-only JSON viewers are retired.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMarketing } from '../MarketingContext';
import { apiFetch } from '../../../utils/apiFetch';
import { Card, Eyebrow, Pill } from '../ui';
import { quarterOf, nextQuarterOf } from '../stages';
import { parseLocalDate } from '../dates';

export default function PlanningView() {
  const [tab, setTab] = useState('strategy');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1 bg-white border border-line rounded-[9px] p-[3px] w-max">
        {[['strategy', 'Strategy'], ['avatars', 'Avatars']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`text-[12.5px] font-bold px-3.5 py-1.5 rounded-[6px] transition-colors ${
              tab === id ? 'bg-deep-teal text-white' : 'text-ink-2 hover:text-navy'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'strategy' ? <StrategyTab /> : <AvatarsTab />}
    </div>
  );
}

// ── Strategy: annual strategies + the quarter beneath them ──────────

function StrategyTab() {
  const { projects } = useMarketing();
  const [strategies, setStrategies] = useState(null);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState(null);

  async function load() {
    try {
      const res = await apiFetch('/api/data/strategy');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setStrategies(json.strategies || []);
    } catch (e) {
      setErr(e.message);
    }
  }
  useEffect(() => { load(); }, []);

  const quarter = quarterOf();
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    apiFetch(`/api/data/content-plan/${quarter}`)
      .then(r => r.json())
      .then(j => setPlan(j.plan || null))
      .catch(() => {});
  }, [quarter]);

  // Projects due inside the current quarter = this 90 days' commitments.
  const quarterProjects = useMemo(() => {
    const m = /^(\d{4})-Q([1-4])$/.exec(quarter);
    const start = new Date(Number(m[1]), (Number(m[2]) - 1) * 3, 1);
    const end = new Date(Number(m[1]), Number(m[2]) * 3, 0);
    return (projects.data || []).filter(p => {
      if (!p.dueDate) return p.status === 'in_progress';
      const d = parseLocalDate(p.dueDate);
      return d >= start && d <= end;
    });
  }, [projects.data, quarter]);

  return (
    <div className="flex flex-col gap-4">
      {err && <div className="text-[12px] text-rose">{err}</div>}
      {!strategies && !err && <div className="text-[12px] text-ink-3">Loading strategies…</div>}

      {strategies?.length === 0 && (
        <Card className="p-5">
          <div className="text-[13px] text-ink-2">
            No strategies in the database yet. Run <code className="font-mono bg-bone/60 px-1 rounded">scripts/migrate-strategy-avatars.mjs</code> to import them.
          </div>
        </Card>
      )}

      {(strategies || []).map(s => (
        <Card key={s.id} lash="navy" className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Eyebrow>Annual strategy{s.year ? ` · ${s.year}` : ''}</Eyebrow>
              <h3 className="text-navy text-[17px] font-black mt-1">{s.name}</h3>
            </div>
            <div className="flex items-center gap-2">
              <Pill tone={s.status === 'active' ? 'good' : 'neutral'}>{s.status}</Pill>
              <button
                onClick={() => setEditing(s)}
                className="text-bullogic-blue text-[11px] font-black border border-bullogic-blue/40 rounded-[6px] px-2.5 py-1 hover:bg-bullogic-blue/5"
              >
                Edit
              </button>
            </div>
          </div>
          {Array.isArray(s.objective) && s.objective.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1.5">
              {s.objective.map((o, i) => (
                <li key={i} className="text-[13px] text-ink flex gap-2">
                  <span className="text-bullogic-blue font-black shrink-0">·</span> {o}
                </li>
              ))}
            </ul>
          )}
          {s.audienceFocus && (
            <div className="mt-3 text-[12.5px] text-ink-2">
              <b className="text-navy">Audience:</b> {s.audienceFocus}
            </div>
          )}
        </Card>
      ))}

      {/* The quarter beneath the strategy: the 90-day commitments. */}
      <Card lash="teal" className="p-4">
        <div className="flex items-center justify-between mb-2.5">
          <Eyebrow>This quarter · {quarter}</Eyebrow>
          <Link to="/marketing/content" className="text-bullogic-blue text-[12px] font-bold">Content plan →</Link>
        </div>
        <div className="text-[13px] text-ink mb-3">
          {plan?.theme
            ? <><b className="text-navy">Theme: {plan.theme}</b>{plan?.kpiTargets?.anchors ? ` · target ${plan.kpiTargets.anchors} anchors` : ''}</>
            : <span className="text-ink-2">No theme set yet. The 90-day ritual starts on the Content Plan tab: set the theme and targets, slot the anchors, then line up the projects below.</span>}
        </div>
        <div className="border-t border-line-2 pt-3">
          <div className="text-[10.5px] font-black uppercase tracking-[0.05em] text-ink-3 mb-2">Projects this quarter</div>
          {quarterProjects.length === 0 ? (
            <div className="text-[12.5px] text-ink-2">
              None yet. Add them on <Link to="/marketing/projects" className="text-bullogic-blue font-bold">Projects</Link> with a due date inside the quarter.
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {quarterProjects.map(p => (
                <div key={p.id} className="flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-ink">{p.name}</span>
                  <Pill tone="neutral">{Math.round(p.progress * 100)}%</Pill>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <div className="bg-bullogic-blue/5 border border-dashed border-bullogic-blue/40 rounded-[7px] px-3.5 py-2.5 text-[12.5px] text-ink-2">
        <b className="text-navy">The cadence:</b> the annual strategy above rarely changes; every 90 days
        (next: {nextQuarterOf()}) set the quarter's theme and targets on the Content Plan, promote ideas
        into weeks, and commit the quarter's projects so the weekly grind serves the strategy.
      </div>

      {editing && (
        <StrategyEditModal
          strategy={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function StrategyEditModal({ strategy, onClose, onSaved }) {
  const [name, setName] = useState(strategy.name || '');
  const [year, setYear] = useState(strategy.year || new Date().getFullYear());
  const [status, setStatus] = useState(strategy.status || 'active');
  const [objectives, setObjectives] = useState((strategy.objective || []).join('\n'));
  const [audienceFocus, setAudienceFocus] = useState(strategy.audienceFocus || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const body = {
        ...strategy,   // preserve any fields this editor doesn't surface
        name: name.trim(),
        year: Number(year) || null,
        status,
        objective: objectives.split('\n').map(l => l.trim()).filter(Boolean),
        audienceFocus: audienceFocus.trim() || null,
      };
      const res = await apiFetch(`/api/data/strategy/${strategy.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const input = 'w-full border border-line rounded-[6px] px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:border-bullogic-blue';
  return (
    <div className="fixed inset-0 z-50 bg-navy/20 grid place-items-center" onClick={onClose}>
      <div className="bg-white border border-line rounded-[10px] shadow-[0_8px_24px_rgba(2,10,40,0.10)] p-4 w-[500px]" onClick={e => e.stopPropagation()}>
        <div className="font-black text-navy text-[14px] mb-3">Edit strategy</div>
        <div className="grid grid-cols-[1fr_90px_120px] gap-2">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className={input} />
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Year</label>
            <input type="number" value={year} onChange={e => setYear(e.target.value)} className={input} />
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className={input}>
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="retired">retired</option>
            </select>
          </div>
        </div>
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1 mt-3">Objectives (one per line)</label>
        <textarea value={objectives} onChange={e => setObjectives(e.target.value)} rows={5} className={input} />
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1 mt-3">Audience focus</label>
        <textarea value={audienceFocus} onChange={e => setAudienceFocus(e.target.value)} rows={2} className={input} />
        {err && <div className="text-[11px] text-rose mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-3.5">
          <button onClick={onClose} className="text-[12px] font-bold text-ink-3 px-3 py-1.5">Cancel</button>
          <button onClick={save} disabled={saving || !name.trim()} className="bg-deep-teal text-white text-[12px] font-black rounded-[6px] px-3.5 py-1.5 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Avatars: stable reference, lightly editable ─────────────────────

function AvatarsTab() {
  const [avatars, setAvatars] = useState(null);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState(null);

  async function load() {
    try {
      const res = await apiFetch('/api/data/avatars');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setAvatars(json.avatars || []);
    } catch (e) {
      setErr(e.message);
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="flex flex-col gap-4">
      {err && <div className="text-[12px] text-rose">{err}</div>}
      {!avatars && !err && <div className="text-[12px] text-ink-3">Loading avatars…</div>}

      {avatars?.length === 0 && (
        <Card className="p-5">
          <div className="text-[13px] text-ink-2">
            No avatars in the database yet. Run <code className="font-mono bg-bone/60 px-1 rounded">scripts/migrate-strategy-avatars.mjs</code> to import them.
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {(avatars || []).map(a => (
          <Card key={a.id} lash="sky" className="p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="text-navy text-[15px] font-black">{a.title}</h3>
              <div className="flex items-center gap-2 shrink-0">
                <Pill tone={a.status === 'active' ? 'good' : 'neutral'} className="text-[10px]">{a.status}</Pill>
                <button
                  onClick={() => setEditing(a)}
                  className="text-bullogic-blue text-[10px] font-black border border-bullogic-blue/40 rounded-[5px] px-2 py-0.5 hover:bg-bullogic-blue/5"
                >
                  Edit
                </button>
              </div>
            </div>
            {Array.isArray(a.details) && (
              <ul className="flex flex-col gap-1">
                {a.details.map((d, i) => (
                  <li key={i} className="text-[12.5px] text-ink flex gap-2">
                    <span className="text-sky-blue font-black shrink-0">·</span> {d}
                  </li>
                ))}
              </ul>
            )}
            {a.outcome && (
              <div className="mt-2.5 pt-2.5 border-t border-line-2 text-[12.5px] text-ink-2">
                <b className="text-navy">Outcome they want:</b> {a.outcome}
              </div>
            )}
          </Card>
        ))}
      </div>

      {editing && (
        <AvatarEditModal
          avatar={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function AvatarEditModal({ avatar, onClose, onSaved }) {
  const [title, setTitle] = useState(avatar.title || '');
  const [status, setStatus] = useState(avatar.status || 'active');
  const [details, setDetails] = useState((avatar.details || []).join('\n'));
  const [outcome, setOutcome] = useState(avatar.outcome || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const body = {
        ...avatar,
        title: title.trim(),
        status,
        details: details.split('\n').map(l => l.trim()).filter(Boolean),
        outcome: outcome.trim() || null,
      };
      const res = await apiFetch(`/api/data/avatars/${avatar.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const input = 'w-full border border-line rounded-[6px] px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:border-bullogic-blue';
  return (
    <div className="fixed inset-0 z-50 bg-navy/20 grid place-items-center" onClick={onClose}>
      <div className="bg-white border border-line rounded-[10px] shadow-[0_8px_24px_rgba(2,10,40,0.10)] p-4 w-[480px]" onClick={e => e.stopPropagation()}>
        <div className="font-black text-navy text-[14px] mb-3">Edit avatar</div>
        <div className="grid grid-cols-[1fr_120px] gap-2">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className={input} />
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className={input}>
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="retired">retired</option>
            </select>
          </div>
        </div>
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1 mt-3">Details (one per line)</label>
        <textarea value={details} onChange={e => setDetails(e.target.value)} rows={6} className={input} />
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1 mt-3">Outcome they want</label>
        <textarea value={outcome} onChange={e => setOutcome(e.target.value)} rows={2} className={input} />
        {err && <div className="text-[11px] text-rose mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-3.5">
          <button onClick={onClose} className="text-[12px] font-bold text-ink-3 px-3 py-1.5">Cancel</button>
          <button onClick={save} disabled={saving || !title.trim()} className="bg-deep-teal text-white text-[12px] font-black rounded-[6px] px-3.5 py-1.5 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
